// FTP estimated from the rider's own rides (ADR 0027): nobody types an FTP. The server computes it from the stored
// activities each time it needs it, so a deleted ride or a new one moves it with nothing to keep in step.
// The parameters come from the FTP bench (tools/ftp-bench, docs/FTP.md): change them there first.
// - Start: a provisional FTP from the weight, kept as a floor until the rider has done a few real rides or a test.
// - Power curve: the best power per duration across the rides, each effort weighted by its age and by the breaks
//   taken since. Easy rides never pull it down, or an ERG workout at 70 % would lower the FTP, then the next workout…
// - From the curve: the best of 0.78 × the 5-minute, 0.90 × the 10-minute, 0.95 × the 20-minute and the hour, or the
//   critical power fitted on the 5, 10 and 20 minute points (work = CP × t + W′) when it is higher. The bench chose
//   these over the fit alone (which non-maximal efforts drag down) and over a generous 5-minute factor (which flatters
//   riders with a big anaerobic capacity).
// - Test: a completed FTP or Ramp test sets it, up or down: the efforts before it no longer count.
// - Test due: when the rides cannot tell (ERG workouts only, a long break, workouts the rider cannot hold), the
//   estimate says so and the app proposes a test. It never lowers the FTP on the client's word.

export interface FtpParams {
  /** Provisional FTP of a rider with no rides yet, in W/kg. */
  seedWkg: number;
  min: number;
  max: number;
  /** Efforts older than this no longer count (days). */
  windowDays: number;
  /** An effort counts in full for this long (days), then loses `decayPerWeek` a week, down to `decayFloor`. */
  fullDays: number;
  decayPerWeek: number;
  decayFloor: number;
  /** A gap of more than `breakDays` without riding costs the efforts before it `breakLossPerDay` per extra day, down to `breakFloor`. */
  breakDays: number;
  breakLossPerDay: number;
  breakFloor: number;
  /** Races and free rides of at least `qualifyS` seconds it takes for the estimate to replace the provisional FTP. */
  establishRides: number;
  qualifyS: number;
  /** Durations fitted by the critical power model (s). */
  cpDurations: readonly number[];
  /** Floor of the fit: share of the 20-minute best (the classic FTP test), and of the hour. */
  floor20: number;
  /**
   * Ceiling of the fit: share of the best at the longest duration fitted. On a maximal power curve the critical power
   * sits at 93 to 97 % of the 20-minute best and 87 to 94 % of the 10-minute one; an evenly paced effort (5, 10 and 20
   * minute bests alike) would otherwise read as an FTP equal to its power.
   */
  caps: Readonly<Record<number, number>>;
  /** FTP-equivalent of the best effort per duration: the rule of thumb the fit is checked against. */
  factors: readonly { dur: number; factor: number }[];
  /** The fit never goes below this share of the rule of thumb (1: the rule of thumb alone, 0: the fit alone). */
  factorShare: number;
  /** Ramp test: FTP = `rampFactor` × the best minute. It counts only past the warm-up and a few steps. */
  rampFactor: number;
  rampMinS: number;
  /** Watts beyond human reach: an effort above this is ignored like any other out-of-bounds power. */
  maxWatts: number;
  /** Test due: after this many rides while provisional; when no effort of the last `staleDays` holds the FTP up. */
  unknownRides: number;
  staleDays: number;
  /** Test due: back from a break of `returnBreakDays` or more, during `returnDays`. */
  returnBreakDays: number;
  returnDays: number;
  /** Test due: `struggleRides` workouts in a row under `struggleCompliance` % of time on target, within `struggleDays`. */
  struggleRides: number;
  struggleCompliance: number;
  struggleDays: number;
  /** No test is proposed this soon after the last one (days). */
  testQuietDays: number;
}

export const FTP: FtpParams = {
  seedWkg: 2.2,
  min: 60,
  max: 600,
  windowDays: 365,
  fullDays: 42,
  decayPerWeek: 0.01,
  decayFloor: 0.85,
  breakDays: 10,
  breakLossPerDay: 0.002,
  breakFloor: 0.8,
  establishRides: 3,
  qualifyS: 600,
  cpDurations: [300, 600, 1200],
  floor20: 0.95,
  caps: { 600: 0.92, 1200: 0.97 },
  factors: [
    { dur: 3600, factor: 1 },
    { dur: 1200, factor: 0.95 },
    { dur: 600, factor: 0.9 },
    { dur: 300, factor: 0.78 },
  ],
  factorShare: 1,
  rampFactor: 0.72,
  rampMinS: 600,
  maxWatts: 2500,
  unknownRides: 2,
  staleDays: 42,
  returnBreakDays: 21,
  returnDays: 14,
  struggleRides: 2,
  struggleCompliance: 75,
  struggleDays: 21,
  testQuietDays: 21,
};

const DAY = 86400000;

/** What the estimate reads of an activity. */
export interface FtpRide {
  date: number;
  type: string;
  summary: { duration: number; bests: Record<number, number> };
  meta: {
    test?: 'ftp20' | 'ramp' | null;
    workoutId?: string | null;
    power?: string | null;
    flags?: readonly string[] | null;
    /** Share of the workout ridden on target (client measure): only ever used to propose a test. */
    compliance?: number | null;
  };
}

/** How the FTP was obtained: weight alone, efforts in rides, or a test (the efforts since it included). */
export type FtpBasis = 'seed' | 'efforts' | 'test';
/** Why a test would help: nothing measured yet, efforts fading, back from a break, workouts not held. */
export type FtpTestReason = 'unknown' | 'stale' | 'return' | 'struggling';
export interface FtpEstimate {
  ftp: number;
  /** Still the weight-based start: not enough rides yet. */
  provisional: boolean;
  basis: FtpBasis;
  /** A test would set the FTP better than the rides can; null when the rides say enough. */
  testDue: FtpTestReason | null;
}
export interface FtpChange { date: number; ftp: number; from: number }

/** Provisional FTP for a weight, rounded to 5 W. */
export const seedFtp = (mass: number, p: FtpParams = FTP): number => clamp(Math.round(((mass > 0 ? mass : 75) * p.seedWkg) / 5) * 5, p);

const clamp = (w: number, p: FtpParams) => Math.max(p.min, Math.min(p.max, Math.round(w)));
const best = (r: FtpRide, dur: number, p: FtpParams) => Math.max(0, Math.min(p.maxWatts, Number(r.summary.bests?.[dur]) || 0));
/** Keyboard watts and a ride the server flagged say nothing of a rider's level (ADR 0022). */
const realPower = (r: FtpRide) => r.meta.power !== 'simulated' && !r.meta.flags?.length;
const isWorkout = (r: FtpRide) => r.type === 'workout';

/** FTP a completed test measures, or null when the ride is not one (or was given up too early to tell). */
export function testFtp(r: FtpRide, p: FtpParams = FTP): number | null {
  if (!realPower(r)) return null;
  if (r.meta.test === 'ftp20' && r.meta.workoutId === 'ftp20' && best(r, 1200, p) > 0) return best(r, 1200, p) * 0.95;
  if (r.meta.test === 'ramp' && r.meta.workoutId === 'ramp' && r.summary.duration >= p.rampMinS && best(r, 60, p) > 0) return best(r, 60, p) * p.rampFactor;
  return null;
}

/** Weight of an effort `ageMs` old. */
export function ageFactor(ageMs: number, p: FtpParams = FTP): number {
  const days = Math.max(0, ageMs) / DAY;
  if (days <= p.fullDays) return 1;
  return Math.max(p.decayFloor, 1 - (p.decayPerWeek * (days - p.fullDays)) / 7);
}

/** Weight of an effort made before breaks: each gap longer than `breakDays` costs the days beyond it. `after`: ride dates after the effort, sorted, then now. */
function breakFactor(after: readonly number[], from: number, p: FtpParams): number {
  let lost = 0, prev = from;
  for (const d of after) { lost += Math.max(0, (d - prev) / DAY - p.breakDays); prev = d; }
  return Math.max(p.breakFloor, 1 - p.breakLossPerDay * lost);
}

/** FTP-equivalent of a power curve by the rule of thumb: the best of each duration's factor, never below the hour. */
export function factorFtp(curve: Record<number, number>, p: FtpParams = FTP): number {
  let w = curve[3600] || 0;
  for (const { dur, factor } of p.factors) w = Math.max(w, (curve[dur] || 0) * factor);
  return w;
}

/**
 * FTP from a power curve (best watts per duration): the critical power fitted by least squares on `work = CP × t + W′`
 * over the durations available, kept under `caps` of the longest effort fitted, floored by `floor20` of the 20-minute best, by the hour
 * and by `factorShare` of the rule of thumb. A non-maximal 20-minute effort drags the fit down a lot; the rule of thumb
 * alone flatters riders with a big anaerobic capacity. The bench weighs the two.
 */
export function curveFtp(curve: Record<number, number>, p: FtpParams = FTP): number {
  const pts = p.cpDurations.filter(d => (curve[d] || 0) > 0).map(d => ({ t: d, w: curve[d] * d }));
  const thumb = factorFtp(curve, p);
  if (pts.length < 2) return thumb;
  const floor = Math.max(p.floor20 * (curve[1200] || 0), curve[3600] || 0, p.factorShare * thumb);
  const n = pts.length, mt = pts.reduce((a, x) => a + x.t, 0) / n, mw = pts.reduce((a, x) => a + x.w, 0) / n;
  const cp = pts.reduce((a, x) => a + (x.t - mt) * (x.w - mw), 0) / pts.reduce((a, x) => a + (x.t - mt) ** 2, 0);
  // critical power sits below the power of the longest effort; a fit above it means the long effort was not maximal
  const longest = pts[n - 1].t, ceiling = (p.caps[longest] ?? 1) * curve[longest];
  return Math.max(floor, Math.min(cp, ceiling));
}

/** A ride the rider chose the effort of: a race or a free ride long enough to mean something. ERG workouts impose it. */
const qualifies = (r: FtpRide, p: FtpParams) => r.type === 'race' && r.summary.duration >= p.qualifyS && best(r, 600, p) > 0;

/** The rider's FTP at `now`, from their rides (any order; later ones and those outside the window are ignored). */
export function estimateFtp(rides: readonly FtpRide[], mass: number, now: number, p: FtpParams = FTP): FtpEstimate {
  const seed = seedFtp(mass, p);
  const counted = rides.filter(r => realPower(r) && r.date <= now && now - r.date <= p.windowDays * DAY).sort((a, b) => a.date - b.date);
  let test: FtpRide | null = null;
  for (const r of counted) if (testFtp(r, p) != null) test = r;
  const since = test ? counted.filter(r => r.date >= test.date) : counted;
  const dates = [...since.map(r => r.date), now];

  // the aged power curve, and the same over the last `staleDays` without ageing (what recent rides alone hold up)
  const curve: Record<number, number> = {}, recent: Record<number, number> = {};
  const durs = [...new Set([...p.cpDurations, ...p.factors.map(f => f.dur)])];
  let tested = 0;
  since.forEach((r, i) => {
    const k = ageFactor(now - r.date, p) * breakFactor(dates.slice(i + 1), r.date, p), t = testFtp(r, p);
    // a test is read by its own protocol: its bests stay out of the curve, where a fit would read them again
    if (t != null) { tested = Math.max(tested, t * k); return; }
    for (const d of durs) {
      curve[d] = Math.max(curve[d] || 0, best(r, d, p) * k);
      if (now - r.date <= p.staleDays * DAY) recent[d] = Math.max(recent[d] || 0, best(r, d, p));
    }
  });
  const w = Math.max(tested, curveFtp(curve, p));
  const established = !!test || since.filter(r => qualifies(r, p)).length >= p.establishRides;
  const est: Omit<FtpEstimate, 'testDue'> = established
    ? { ftp: clamp(w, p), provisional: false, basis: test ? 'test' : 'efforts' }
    : w > seed ? { ftp: clamp(w, p), provisional: true, basis: 'efforts' } : { ftp: seed, provisional: true, basis: 'seed' };
  return { ...est, testDue: testDue(counted, test, est, curveFtp(recent, p), now, p) };
}

function testDue(rides: readonly FtpRide[], test: FtpRide | null, est: Omit<FtpEstimate, 'testDue'>, recentFtp: number, now: number, p: FtpParams): FtpTestReason | null {
  if (test && now - test.date < p.testQuietDays * DAY) return null;
  const since = test ? rides.filter(r => r.date > test.date) : rides;
  const workouts = since.filter(r => isWorkout(r) && !r.meta.test && now - r.date <= p.struggleDays * DAY).slice(-p.struggleRides);
  if (workouts.length === p.struggleRides && workouts.every(r => r.meta.compliance != null && r.meta.compliance < p.struggleCompliance)) return 'struggling';
  for (let i = 1; i < since.length; i++) {
    if (now - since[i].date > p.returnDays * DAY) continue;
    if (since[i].date - since[i - 1].date >= p.returnBreakDays * DAY) return 'return';
  }
  if (est.provisional && since.length >= p.unknownRides) return 'unknown';
  // the FTP rests on efforts that are fading: the last weeks of riding no longer hold it up
  const riding = since.some(r => now - r.date <= p.returnDays * DAY);
  if (!est.provisional && riding && recentFtp < 0.97 * est.ftp) return 'stale';
  return null;
}

/**
 * The FTP each ride left behind, as changes (oldest first, the last `limit`): what the progress screen shows. The
 * seed comes from the current weight, which is the only one known.
 */
export function ftpHistory(rides: readonly FtpRide[], mass: number, limit = 50, p: FtpParams = FTP): FtpChange[] {
  const sorted = [...rides].sort((a, b) => a.date - b.date), out: FtpChange[] = [];
  let prev = seedFtp(mass, p);
  for (let i = 0; i < sorted.length; i++) {
    const { ftp } = estimateFtp(sorted.slice(0, i + 1), mass, sorted[i].date, p);
    if (ftp !== prev) out.push({ date: sorted[i].date, ftp, from: prev });
    prev = ftp;
  }
  return out.slice(-limit);
}
